import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  IconButton,
  Checkbox,
  FormGroup,
  FormLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { servicesStore, employeesManagementStore } from '../stores/AdminStore';

const ServiceDialog = observer(({ open, onClose, service }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    durationMinutes: 30,
    price: 0,
    active: true,
    employees: []
  });

  // Load all employees when dialog opens (for service assignment)
  useEffect(() => {
    if (open) {
      employeesManagementStore.fetchEmployees();
    }
  }, [open]);

  useEffect(() => {
    if (service) {
      // Filter out employees that no longer exist
      const validEmployeeIds = (service.employees || []).filter(empId =>
        employeesManagementStore.employees?.some(emp => emp.id === empId)
      );

      setFormData({
        name: service.name || '',
        description: service.description || '',
        durationMinutes: service.durationMinutes || 30,
        price: service.price || 0,
        active: service.active !== false,
        employees: validEmployeeIds
      });
    } else {
      // For new services, auto-select all employees
      const allEmployeeIds = employeesManagementStore.employees ? employeesManagementStore.employees.map(emp => emp.id) : [];
      setFormData({
        name: '',
        description: '',
        durationMinutes: 30,
        price: 0,
        active: true,
        employees: allEmployeeIds
      });
    }
  }, [service, open]);

  const handleSubmit = async () => {
    const success = service
      ? await servicesStore.updateService(service.id, formData)
      : await servicesStore.createService(formData);

    if (success) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {service ? 'Editar Serviço' : 'Novo Serviço'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Descrição"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Duração (minutos)"
            type="number"
            value={formData.durationMinutes}
            onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) })}
            required
            fullWidth
          />
          <TextField
            label="Preço (R$)"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
            required
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Ativo"
          />

          {/* Employee Selection */}
          <Box>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Profissionais que realizam este serviço
            </FormLabel>
            {!employeesManagementStore.employees || employeesManagementStore.employees.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum profissional cadastrado
              </Typography>
            ) : (
              <FormGroup>
                {employeesManagementStore.employees.map((employee) => (
                  <FormControlLabel
                    key={employee.id}
                    control={
                      <Checkbox
                        checked={formData.employees.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              employees: [...formData.employees, employee.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              employees: formData.employees.filter(id => id !== employee.id)
                            });
                          }
                        }}
                      />
                    }
                    label={employee.name}
                  />
                ))}
              </FormGroup>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={servicesStore.isLoading}>
          {service ? 'Atualizar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const ServicesManagement = observer(() => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    servicesStore.loadServices();
    employeesManagementStore.fetchEmployees();
  }, []);

  useEffect(() => {
    if (servicesStore.successMessage) {
      setSnackbarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesStore.successMessage]);

  const handleEdit = (service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedService(null);
    setDialogOpen(true);
  };

  const handleDelete = async (service) => {
    if (window.confirm(`Tem certeza que deseja remover o serviço "${service.name}"?`)) {
      await servicesStore.deleteService(service.id);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedService(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    servicesStore.clearMessages();
  };

  const columns = [
    { field: 'name', headerName: 'Nome', flex: 1 },
    {
      field: 'formattedEmployeeNames',
      headerName: 'Profissionais',
      flex: 1,
      renderCell: (params) => {
        const employeeNames = params.value || [];
        if (employeeNames.length === 0) {
          return <Typography variant="body2" color="text.secondary">Nenhum</Typography>;
        }

        return (
          <Box sx={{ py: 1 }}>
            {employeeNames.map((name, index) => (
              <Typography key={index} variant="body2" sx={{ lineHeight: 1.5 }}>
                {name}
              </Typography>
            ))}
          </Box>
        );
      }
    },
    { field: 'description', headerName: 'Descrição', flex: 1.5 },
    {
      field: 'durationMinutes',
      headerName: 'Duração (min)',
      width: 130,
      type: 'number'
    },
    {
      field: 'price',
      headerName: 'Preço',
      width: 100,
      valueFormatter: (params) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(params);
      }
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <span style={{ color: params.value ? 'green' : 'red' }}>
          {params.value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          Gerenciar Serviços
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Novo Serviço
        </Button>
      </Box>

      {servicesStore.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {servicesStore.error}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={servicesStore.services}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={servicesStore.isLoading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      <ServiceDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        service={selectedService}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {servicesStore.successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ServicesManagement;
